import React, { useEffect, useRef } from 'react'
import { useState } from 'react'

import { Box, Card, CardContent, CardHeader } from '@material-ui/core'
import { Popover } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import Measure from 'react-measure'

import ErrorMessage from '../../../../components/ErrorMessage'
import Legend from './Legend'
import Loading from './Loading'
import TooltipContent from './TooltipContent'
import { renderChart } from './render-chart'

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    height: 64,
    boxShadow: `0px 1px 0px ${theme.palette.grey[200]}`,
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    height: 250,
  },
  measure: {
    width: '100%',
  },
  chartContainer: {
    width: '100%',
    marginBottom: theme.spacing(4),
  },
}))
const debouncedResize = debounce(renderChart, 200)

const ClassYearMetric = ({ title, seniorGradYear, total, gradYearCounts, loading, error }) => {
  const [popoverData, setPopoverData] = useState(null)
  const container = useRef()
  const styles = useStyles()
  const handleMouseOver = (gradYear, value, element) => setPopoverData({ gradYear, value, element })
  const handleMouseLeave = () => setPopoverData(null)

  useEffect(() => {
    if (loading || error) {
      return
    }
    renderChart(container.current, seniorGradYear, total, gradYearCounts, handleMouseOver, handleMouseLeave)
  }, [container, seniorGradYear, total, gradYearCounts, loading, error])
  return (
    <React.Fragment>
      {popoverData && (
        <Popover
          open={Boolean(popoverData.element)}
          anchorEl={popoverData.element}
          onClose={() => setPopoverData(null)}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          style={{
            pointerEvents: 'none',
          }}
          disableRestoreFocus
        >
          <TooltipContent
            gradYear={popoverData.gradYear}
            seniorGradYear={seniorGradYear}
            value={popoverData.value}
          />
        </Popover>
      )}
      <Measure
        onResize={(contentRect) => {
          if (contentRect && contentRect.entry) {
            debouncedResize(container.current, seniorGradYear, total, gradYearCounts, handleMouseOver, handleMouseLeave)
          }
        }}
      >
        {({ measureRef }) => (
          <Card elevation={0}>
            <CardHeader
              classes={{ root: styles.cardHeader }}
              title={title}
              titleTypographyProps={{ variant: 'body1', color: 'textPrimary' }}
            />
            <CardContent className={styles.cardContent}>
              {loading ? (
                <Loading />
              ) : error ? (
                <ErrorMessage />
              ) : (
                <Box
                  mx={6}
                  ref={measureRef}
                  className={styles.measure}
                >
                  <div
                    className={styles.chartContainer}
                    ref={container}
                  />
                  <Legend
                    seniorGradYear={seniorGradYear}
                    gradYearCounts={gradYearCounts}
                    total={total}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Measure>
    </React.Fragment>
  )
}

ClassYearMetric.propTypes = {
  title: PropTypes.string.isRequired,
  seniorGradYear: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  gradYearCounts: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
}

export default ClassYearMetric
